import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Globe, MapPin, Instagram, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/common';

export const Footer = () => {
  const { language } = useLanguage();
  const t = translations[language].footer;
  const nav = translations[language].header;

  return (
    <footer className="bg-gradient-to-br from-gray-50 to-teal-50 border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Main Info */}
        <div className="text-center mb-12">
          <h3 className="text-2xl font-light text-gray-900 mb-2">
            {t.name}
          </h3>
          <p className="text-gray-700 mb-4">
            {t.tagline}
          </p>
          <div className="flex items-center justify-center space-x-2 text-gray-600 mb-6">
            <MapPin size={18} className="text-teal-600" />
            <span>{t.location}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-4">{t.contact}</h4>
            <ul className="space-y-3">
              <li className="flex items-center space-x-2 text-sm text-gray-600">
                <Mail size={16} className="text-teal-600" />
                <a href="mailto:stefruiz04@gmail.com" className="hover:text-teal-600 transition-colors">
                  stefruiz04@gmail.com
                </a>
              </li>
              <li className="flex items-center space-x-2 text-sm text-gray-600">
                <Phone size={16} className="text-teal-600" />
                <a href="tel:+34608414296" className="hover:text-teal-600 transition-colors">
                  +34 608 414 296
                </a>
              </li>
              <li className="flex items-center space-x-2 text-sm text-gray-600">
                <Globe size={16} className="text-teal-600" />
                <span>www.comprendre-qui-on-est.com</span>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-4">{t.navigation}</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm text-gray-600 hover:text-teal-600 transition-colors">
                  {nav.home}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm text-gray-600 hover:text-teal-600 transition-colors">
                  {nav.about}
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-sm text-gray-600 hover:text-teal-600 transition-colors">
                  {nav.services}
                </Link>
              </li>
              <li>
                <Link to="/booking" className="text-sm text-gray-600 hover:text-teal-600 transition-colors">
                  {nav.booking}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-gray-600 hover:text-teal-600 transition-colors">
                  {nav.contact}
                </Link>
              </li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-4">{t.follow}</h4>
            <div className="flex space-x-4">
              <a
                href="https://www.instagram.com/comprendre_qui_on_est"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white hover:scale-110 transition-transform duration-300"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://wa.me/34608414296?text=Bonjour%20Stef,%20je%20souhaite%20obtenir%20des%20informations%20sur%20vos%20accompagnements."
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white hover:scale-110 transition-transform duration-300"
                aria-label="WhatsApp"
              >
                <MessageCircle size={20} />
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-200 pt-8 text-center">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-4">
            <Link to="/mentions-legales" data-testid="footer-legal-link" className="text-sm text-gray-600 hover:text-teal-600 transition-colors">
              {t.legalNotice}
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/confidentialite" data-testid="footer-privacy-link" className="text-sm text-gray-600 hover:text-teal-600 transition-colors">
              {t.privacy}
            </Link>
          </div>
          <p className="text-sm text-gray-600">
            {t.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
};
